import { useId, type ReactNode } from 'react';
import { formatNumber } from '../../format/format.ts';
import type { StatusTone } from '../../status-colors.ts';
import styles from './ChoiceChips.module.css';

export type Choice<Value extends string> = {
  readonly value: Value;
  readonly label: string;
  readonly count?: number | undefined;
  readonly tone?: ChoiceTone | undefined;
  readonly startsGroup?: boolean | undefined;
};

export type ChoiceTone = StatusTone | 'accent';

const accessibleName = <Value extends string>(choice: Choice<Value>, locale: string): string =>
  choice.count === undefined ? choice.label : `${choice.label} (${formatNumber(choice.count, locale)})`;

export type ChoiceChipsProps<Value extends string> = {
  legend: string;
  choices: readonly Choice<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  locale: string;
  helperText?: string | undefined;
  hideLegend?: boolean;
  trailing?: ReactNode;
};

export const ChoiceChips = <Value extends string>({
  legend,
  choices,
  value,
  onChange,
  locale,
  helperText,
  hideLegend = false,
  trailing,
}: ChoiceChipsProps<Value>) => {
  const name = useId();
  return (
    <fieldset className={styles['group']} aria-describedby={helperText ? `${name}-helper` : undefined}>
      <legend className={hideLegend ? 'visually-hidden' : styles['legend']}>{legend}</legend>
      <div className={styles['chips']}>
        {choices.map((choice) => (
          <label
            key={choice.value}
            className={styles['chip']}
            data-starts-group={choice.startsGroup === true}
          >
            <input
              type="radio"
              name={name}
              value={choice.value}
              checked={choice.value === value}
              aria-label={accessibleName(choice, locale)}
              className={styles['input']}
              onChange={() => {
                onChange(choice.value);
              }}
            />
            <span className={styles['face']}>
              {choice.tone === undefined ? null : (
                <span className={[styles['dot'], styles[choice.tone]].join(' ')} aria-hidden="true" />
              )}
              {choice.label}
              {choice.count === undefined ? null : (
                <span className={styles['count']} aria-hidden="true">
                  {formatNumber(choice.count, locale)}
                </span>
              )}
            </span>
          </label>
        ))}
        {trailing}
      </div>
      {helperText ? (
        <span id={`${name}-helper`} className={styles['helper']}>
          {helperText}
        </span>
      ) : null}
    </fieldset>
  );
};
